import { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { MinhaContaActions } from 'Store/MinhaConta/MinhaConta.actions';
import { IUserInfoResponse } from 'Data/interfaces/MinhaConta/IUserInfoResponse';
import schema from './updateConta.schema'
import { yupResolver } from '@hookform/resolvers/yup';
import FieldControled from 'Components/UI/FieldControled';
import Modal from 'Components/UI/Modal';
import Button from 'Components/UI/Button';
import Select from 'Components/UI/Select';
import UploadAvatar from 'Components/UI/UploadAvatar';
import { Icon } from 'Components/UI';
import toastHandler from 'Utils/toastHandler';
import { stringToPhone } from 'Utils/MaskUtils';
import { isObjectEmpty } from 'Utils/ObjectUtils';
import styles from './UpdateConta.module.scss';

interface IModal {
  visible?: boolean;
  isLoading?: boolean;
  userInfos: IUserInfoResponse;
  companyPositions: any;
  onCancel?: () => void;
}

const UpdateConta: FC<IModal> = ({
  visible,
  isLoading,
  userInfos,
  companyPositions,
  onCancel,
}) => {
  const dispatch = useDispatch();

  const [imageUser, setImageUser] = useState<string>(userInfos?.OriginalImageUrl);
  const [updateImageUser, setUpdateImageUser] = useState<boolean>(false);
  const [positionCompany, setPositionCompany] = useState<any>(userInfos.CompanyPosition?.Name);
  const [updatePositionCompany, setUpdatePositionCompany] = useState<boolean>(false);
  const positionsStudent = [
    "Estudante de Engenharia", 
    "Estudante de Arquitetura", 
    "Estagiário", 
    6, 
    13, 
    14
  ];

  const {
    control,
    handleSubmit,
    trigger,
    formState: { errors },
  } = useForm({
    defaultValues: {
      Nome: userInfos?.Nome,
      EmailAlternativo: userInfos?.EmailAlternativo,
      Telefone: stringToPhone(userInfos?.Telefone),
      Faculdade: userInfos?.Faculdade,
    },
    resolver: yupResolver(schema),
  });

  const onUploadImageUser = (uploadedImage: string) => {
    setImageUser(uploadedImage);
    setUpdateImageUser(true);
  };

  useEffect(() => {
    setImageUser(userInfos?.OriginalImageUrl);
    setPositionCompany(userInfos.CompanyPosition?.Name);
  }, [userInfos.CompanyPosition?.Name, userInfos?.OriginalImageUrl]);


  const setNewPositionCompany = (value: any) => {
    setPositionCompany(value);
    setUpdatePositionCompany(true);
  }

  const updateDadosConta = (data?: any) => {
    if (!positionCompany) {
      toastHandler.showError('Informe o cargo');
      return;
    }

    if (data) {
      data.Telefone = data.Telefone.replace(/\D/g, '');
      if (!updatePositionCompany) {
        data.AvatarFileName = imageUser === ''
          ? ''
          : imageUser?.substr(imageUser?.lastIndexOf("/") + 1);
        const companyPositionIndex = companyPositions.findIndex((obj: any) => {
          return obj.description === positionCompany
        });
        if (companyPositionIndex !== -1) {
          data.CompanyPositionId = companyPositions[companyPositionIndex].value;
        } else {
          data.CompanyPositionId = 0
        }
      } else {
        data.CompanyPositionId = positionCompany;
      }
      if (!data.EmailAlternativo) {
        data.EmailAlternativo = null;
      }
      dispatch(MinhaContaActions.updateDadosPessoais(data));
      setUpdatePositionCompany(false);
      setUpdateImageUser(false);
    }
  };

  const buttons = (
    <div>
      <Button height={37} onClick={onCancel}>
        <span style={{ padding: '0 20px' }}>
          Cancelar
        </span>
      </Button>
      <Button
        type="primary"
        height={37}
        loading={isLoading}
        disabled={isLoading || !isObjectEmpty(errors)}
        onClick={handleSubmit(updateDadosConta)}
      >
        <span style={{ padding: '0 18px' }}>
          Atualizar
        </span>
      </Button>
    </div>
  );

  return (
    <Modal 
      title="Detalhes da conta" 
      footerButtons={buttons} 
      visible={visible} 
      width={798} 
      onCancel={onCancel}
    >
      <div className={styles['boxDetalhesConta']}>
        <div className={styles['DetalhesContaLeft']}>
          <UploadAvatar onUploadImage={onUploadImageUser}>
            {(isLoading: boolean) => {
              return (
                <div className={styles['changeImageWrapper']} style={{
                  backgroundImage: imageUser
                    ? `linear-gradient(rgba(46,163,242, .6), rgba(46,163,242, .6)),
                          url('${updateImageUser ? process.env.REACT_APP_IMAGE_STORAGE_URL : ''}${imageUser}')`
                    : '',
                }}>
                  {isLoading ? (
                    <Spin indicator={<LoadingOutlined color='white' style={{ fontSize: 40, color: 'white' }} />} />
                  ) : (
                    <>
                      <Icon color='branco' customSize={19} icon='editarImg' />
                      <span className={styles['changeImageLabel']}>
                        Alterar imagem
                      </span>
                    </>
                  )}
                </div>);
            }}
          </UploadAvatar>
        </div>
        <div className={styles['DetalhesContaRight']}>
          <div className={styles['boxData']}>
            <form>
              <FieldControled
                className={styles['fields']}
                name="Nome"
                label="Nome"
                placeholder="Insira seu nome"
                control={control}
                trigger={trigger}
              />
              <FieldControled
                className={styles['fields']}
                name="EmailAlternativo"
                label="E-mail alternativo"
                placeholder="Insira seu e-mail alternativo"
                control={control}
                trigger={trigger}
              />
              <FieldControled
                className={styles['fields']}
                name="Telefone"
                label="Telefone"
                maxLenght={15}
                customMask
                control={control}
                trigger={trigger}
              />
              <Select
                size="large"
                onSelect={setNewPositionCompany}
                className={styles['selectPositionCompany']}
                placeholder="Qual seu cargo?"
                options={companyPositions}
                defaultValue={positionCompany}
              />
              <FieldControled
                className={styles['fields']}
                style={{
                  visibility: positionsStudent.indexOf(positionCompany, 0) !== -1
                    ? 'visible'
                    : 'hidden',
                  marginTop: '28px'
                }}
                name="Faculdade"
                label="Faculdade"
                control={control}
                trigger={trigger}
              />
            </form>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default UpdateConta;
